/**
 * Contractors & Workers management.
 * Manage contractor firms and their on-site workers. Workers can be assigned to
 * permits (PTW) and hold certifications (Certifications screen, Contractor type).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Users, Building2, Plus, Edit2, Trash2, X, Loader2, ArrowLeft, Download, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import TabNavigation from '../../components/TabNavigation';
import { useAlert } from '../../context/AlertContext';
import { useSite } from '../../context/SiteContext';
import { stripDigits, sanitizeMobile, mobileMaxDigits } from '../../utils/inputSanitizers';
import { defaultPhoneCountry } from '../../components/PhoneInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const EMPTY_COMPANY = { name: '', registration_no: '', contact_person: '', contact_email: '', contact_phone: '', status: 'active', wc_policy_no: '', wc_policy_expiry: '', esic_no: '', esic_expiry: '', insurance_expiry: '', agreement_expiry: '' };
const EMPTY_WORKER = { name: '', contractor_company_id: '', contractor_company: '', trade: '', badge_no: '', phone: '', email: '', id_proof_type: '', id_proof_number: '', status: 'active', gate_pass_valid_from: '', gate_pass_valid_to: '', induction_done: false, induction_date: '', certifications: [] };

const ContractorManagement = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showSuccess, showError, confirm } = useAlert();
  const { activeSiteId, activeSiteName } = useSite();
  const [tab, setTab] = useState('workers');
  const [companies, setCompanies] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);   // {type:'company'|'worker', data, editId}
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const siteParam = activeSiteId ? `?site_id=${activeSiteId}` : '';
      const [c, w] = await Promise.all([
        axios.get(`${API}/contractors/companies`, authHeaders()),
        axios.get(`${API}/contractors/workers${siteParam}`, authHeaders()),
      ]);
      setCompanies(c.data.companies || []);
      setWorkers(w.data.workers || []);
      setCanManage(!!(w.data.can_manage || c.data.can_manage));
    } catch (e) {
      showError('Failed to load contractors');
    } finally {
      setLoading(false);
    }
  }, [activeSiteId, showError]);

  useEffect(() => { load(); }, [load]);

  const openNew = (type) => setModal({
    type,
    data: type === 'company' ? { ...EMPTY_COMPANY } : { ...EMPTY_WORKER, site_id: activeSiteId || null, site_name: activeSiteName || '' },
    editId: null,
  });
  const openEdit = (type, row) => setModal({ type, data: { ...row }, editId: row.id });

  const save = async () => {
    const { type, data, editId } = modal;
    // Worker certificates: drop unnamed rows; block past-dated expiry.
    let payload = data;
    if (type === 'worker') {
      const today = new Date().toISOString().slice(0, 10);
      const certs = (data.certifications || []).filter((c) => (c.name || '').trim());
      if (certs.some((c) => c.expiry_date && c.expiry_date < today)) {
        showError('A certificate expiry date is in the past. Please correct it before saving.');
        return;
      }
      payload = { ...data, certifications: certs };
    }
    setSaving(true);
    try {
      const base = type === 'company' ? `${API}/contractors/companies` : `${API}/contractors/workers`;
      if (editId) await axios.put(`${base}/${editId}`, payload, authHeaders());
      else await axios.post(base, payload, authHeaders());
      showSuccess(`Contractor ${type} ${editId ? 'updated' : 'added'}`);
      setModal(null);
      load();
    } catch (e) {
      showError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (type, row) => {
    const ok = await confirm('Delete', `Remove ${row.name}?`, { destructive: true, confirmLabel: 'Delete' });
    if (!ok) return;
    try {
      const base = type === 'company' ? `${API}/contractors/companies` : `${API}/contractors/workers`;
      await axios.delete(`${base}/${row.id}`, authHeaders());
      showSuccess('Deleted');
      load();
    } catch (e) {
      showError(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const isCompanies = tab === 'companies';
  const activeList = isCompanies ? companies : workers;
  const totalPages = Math.max(1, Math.ceil(activeList.length / PAGE_SIZE));
  const pagedList = activeList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [tab]);            // reset to first page on tab switch
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const downloadTemplate = async () => {
    const path = isCompanies ? 'companies/template' : 'workers/template';
    const filename = isCompanies ? 'contractor_companies_template.csv' : 'contractor_workers_template.csv';
    try {
      const res = await axios.get(`${API}/contractors/${path}`, { ...authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showError('Failed to download template');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';   // allow re-selecting the same file
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      let path = 'companies/import';
      if (!isCompanies) {
        const params = new URLSearchParams();
        if (activeSiteId) params.set('site_id', activeSiteId);
        if (activeSiteName) params.set('site_name', activeSiteName);
        path = `workers/import${params.toString() ? `?${params.toString()}` : ''}`;
      }
      const res = await axios.post(`${API}/contractors/${path}`, fd, {
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data || {};
      if (d.failed) {
        showError(`${d.message}${d.errors?.length ? ` — ${d.errors.slice(0, 3).join(' | ')}` : ''}`);
      } else {
        showSuccess(d.message || 'Import complete');
      }
      load();
    } catch (err) {
      showError(err?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const setField = (k, v) => setModal((m) => ({ ...m, data: { ...m.data, [k]: v } }));
  const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6 max-w-6xl mx-auto">
        {/* Back to the Safety hub, which is where this screen is now reached
            from. It used to return to PTW because the only way in was a button
            on the permit list — that button is gone, so returning there would
            send the user somewhere they had not come from. */}
        <button onClick={() => navigate('/safety')} className="mb-4 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" /> Back to Safety Hub
        </button>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Workers &amp; Contractors</h1>
            <p className="text-sm text-gray-500">Manage contractor firms and their on-site workers.</p>
          </div>
          {canManage && (
            <div className="flex items-center gap-2">
              <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleImportFile} />
              <button onClick={downloadTemplate}
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm hover:bg-gray-50"
                title={`Download a sample CSV for ${isCompanies ? 'companies' : 'workers'}`}>
                <Download className="w-4 h-4" /> Template
              </button>
              <button onClick={() => fileInputRef.current?.click()} disabled={importing}
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
                title="Bulk import from a filled-in CSV template">
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import CSV
              </button>
              <button onClick={() => openNew(isCompanies ? 'company' : 'worker')}
                className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                <Plus className="w-4 h-4" /> Add {isCompanies ? 'Company' : 'Worker'}
              </button>
            </div>
          )}
        </div>

        <div className="inline-flex rounded-lg border border-gray-200 bg-white overflow-hidden text-sm mb-4">
          <button onClick={() => setTab('workers')} className={`flex items-center gap-1.5 px-4 py-2 ${tab === 'workers' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}>
            <Users className="w-4 h-4" /> Workers ({workers.length})
          </button>
          <button onClick={() => setTab('companies')} className={`flex items-center gap-1.5 px-4 py-2 border-l border-gray-200 ${tab === 'companies' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}>
            <Building2 className="w-4 h-4" /> Companies ({companies.length})
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            {tab === 'workers' ? (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-2">Name</th><th className="px-4 py-2">Firm</th><th className="px-4 py-2">Trade</th>
                    <th className="px-4 py-2">Badge</th><th className="px-4 py-2">Gate pass to</th><th className="px-4 py-2">Induction</th>
                    <th className="px-4 py-2">Status</th>{canManage && <th className="px-4 py-2 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {workers.length === 0 ? (
                    <tr><td colSpan={canManage ? 8 : 7} className="px-4 py-8 text-center text-gray-500">No contractor workers yet.</td></tr>
                  ) : pagedList.map((w) => (
                    <tr key={w.id} className="hover:bg-gray-50">
                      {/* The name opens the worker, where eligibility and its
                          reasons live. Only the name is a link, not the whole
                          row: the row also carries edit and delete buttons, and
                          a row-wide click target would fire on those too. */}
                      <td className="px-4 py-2 font-medium">
                        <button
                          type="button"
                          onClick={() => navigate(`/contractors/workers/${w.id}`)}
                          className="text-left text-blue-700 hover:underline"
                          title="Open worker — induction, training, medical, eligibility"
                        >
                          {w.name}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        {/* Linked only when the worker actually carries the FK.
                            A denormalised name with no id has nothing to open,
                            and a dead link is worse than plain text. */}
                        {w.contractor_company_id ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/contractors/${w.contractor_company_id}`)}
                            className="text-left text-blue-700 hover:underline"
                          >
                            {w.contractor_company || 'Contractor'}
                          </button>
                        ) : (w.contractor_company || '—')}
                      </td>
                      <td className="px-4 py-2 text-gray-700">{w.trade || '—'}</td>
                      <td className="px-4 py-2 text-gray-700">{w.badge_no || '—'}</td>
                      <td className="px-4 py-2 text-gray-700">{fmtDate(w.gate_pass_valid_to)}</td>
                      <td className="px-4 py-2">{w.induction_done ? <span className="text-green-600">Done</span> : <span className="text-gray-400">Pending</span>}</td>
                      <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${w.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{w.status}</span></td>
                      {canManage && (
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <button onClick={() => openEdit('worker', w)} className="text-blue-600 hover:text-blue-900 mr-3"><Edit2 className="w-4 h-4 inline" /></button>
                          <button onClick={() => remove('worker', w)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4 inline" /></button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-2">Name</th><th className="px-4 py-2">Contact</th><th className="px-4 py-2">WC policy exp.</th>
                    <th className="px-4 py-2">Insurance exp.</th><th className="px-4 py-2">Status</th>{canManage && <th className="px-4 py-2 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {companies.length === 0 ? (
                    <tr><td colSpan={canManage ? 6 : 5} className="px-4 py-8 text-center text-gray-500">No contractor companies yet.</td></tr>
                  ) : pagedList.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">
                        <button
                          type="button"
                          onClick={() => navigate(`/contractors/${c.id}`)}
                          className="text-left text-blue-700 hover:underline"
                          title="Open contractor — compliance, workers, site assignments"
                        >
                          {c.name}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-gray-700">{c.contact_person || c.contact_email || '—'}</td>
                      <td className="px-4 py-2 text-gray-700">{fmtDate(c.wc_policy_expiry)}</td>
                      <td className="px-4 py-2 text-gray-700">{fmtDate(c.insurance_expiry)}</td>
                      <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{c.status}</span></td>
                      {canManage && (
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <button onClick={() => openEdit('company', c)} className="text-blue-600 hover:text-blue-900 mr-3"><Edit2 className="w-4 h-4 inline" /></button>
                          <button onClick={() => remove('company', c)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4 inline" /></button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {activeList.length > PAGE_SIZE && (
              <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
                <span>
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, activeList.length)} of {activeList.length}
                </span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    Previous
                  </button>
                  <span className="text-gray-500">Page {page} of {totalPages}</span>
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-bold text-gray-900">{modal.editId ? 'Edit' : 'Add'} {modal.type === 'company' ? 'Company' : 'Worker'}</h2>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              {/* COMPANY name — digits are legitimate and common: 3M, 24/7 Services,
                  ABC 123 Engineering, L&T Construction 2. stripDigits belongs on
                  PERSON names (below), not here: it silently deleted characters
                  as the user typed, with no message explaining why. */}
              <Field label="Name *" className="col-span-2" value={modal.data.name} onChange={(v) => setField('name', v)} />
              {modal.type === 'company' ? (
                <>
                  <Field label="Contact person" value={modal.data.contact_person} onChange={(v) => setField('contact_person', stripDigits(v))} />
                  <Field label="Contact email" value={modal.data.contact_email} onChange={(v) => setField('contact_email', v)} />
                  {/* REG-13: digit cap follows the tenant phone country (10 for India / legacy, 11 for Malaysia) */}
                  <Field label="Contact phone" value={modal.data.contact_phone} onChange={(v) => setField('contact_phone', sanitizeMobile(v, mobileMaxDigits(defaultPhoneCountry())))} />
                  <Field label="Registration no." value={modal.data.registration_no} onChange={(v) => setField('registration_no', v)} />
                  <Field label="WC policy no." value={modal.data.wc_policy_no} onChange={(v) => setField('wc_policy_no', v)} />
                  <Field label="WC policy expiry" type="date" value={fmtDate(modal.data.wc_policy_expiry) === '—' ? '' : fmtDate(modal.data.wc_policy_expiry)} onChange={(v) => setField('wc_policy_expiry', v)} />
                  <Field label="ESIC no." value={modal.data.esic_no} onChange={(v) => setField('esic_no', v)} />
                  <Field label="ESIC expiry" type="date" value={fmtDate(modal.data.esic_expiry) === '—' ? '' : fmtDate(modal.data.esic_expiry)} onChange={(v) => setField('esic_expiry', v)} />
                  <Field label="Insurance expiry" type="date" value={fmtDate(modal.data.insurance_expiry) === '—' ? '' : fmtDate(modal.data.insurance_expiry)} onChange={(v) => setField('insurance_expiry', v)} />
                  <Field label="Agreement expiry" type="date" value={fmtDate(modal.data.agreement_expiry) === '—' ? '' : fmtDate(modal.data.agreement_expiry)} onChange={(v) => setField('agreement_expiry', v)} />
                  <SelectField label="Status" value={modal.data.status} onChange={(v) => setField('status', v)} options={['active', 'suspended', 'blacklisted']} />
                </>
              ) : (
                <>
                  <SelectField label="Firm" value={modal.data.contractor_company_id || ''} onChange={(v) => {
                    const co = companies.find((c) => c.id === v);
                    setModal((m) => ({ ...m, data: { ...m.data, contractor_company_id: v, contractor_company: co ? co.name : '' } }));
                  }} options={companies.map((c) => ({ value: c.id, label: c.name }))} allowBlank />
                  <Field label="Trade" value={modal.data.trade} onChange={(v) => setField('trade', v)} />
                  <Field label="Badge / gate-pass no." value={modal.data.badge_no} onChange={(v) => setField('badge_no', v)} />
                  <Field label="Phone" value={modal.data.phone} onChange={(v) => setField('phone', sanitizeMobile(v, mobileMaxDigits(defaultPhoneCountry())))} />
                  <Field label="Email" value={modal.data.email} onChange={(v) => setField('email', v)} />
                  <Field label="ID proof type" value={modal.data.id_proof_type} onChange={(v) => setField('id_proof_type', v)} />
                  <Field label="ID proof number (encrypted)" value={modal.data.id_proof_number} onChange={(v) => setField('id_proof_number', v)} />
                  <Field label="Gate pass from" type="date" value={fmtDate(modal.data.gate_pass_valid_from) === '—' ? '' : fmtDate(modal.data.gate_pass_valid_from)} onChange={(v) => setField('gate_pass_valid_from', v)} />
                  <Field label="Gate pass to" type="date" value={fmtDate(modal.data.gate_pass_valid_to) === '—' ? '' : fmtDate(modal.data.gate_pass_valid_to)} onChange={(v) => setField('gate_pass_valid_to', v)} />
                  <SelectField label="Status" value={modal.data.status} onChange={(v) => setField('status', v)} options={['active', 'inactive', 'blacklisted']} />
                  <label className="col-span-2 flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={!!modal.data.induction_done} onChange={(e) => setField('induction_done', e.target.checked)} />
                    Safety induction completed
                  </label>

                  {/* Certificates — auto-populate into a PTW when this worker is selected */}
                  <div className="col-span-2 border-t border-gray-200 pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold text-gray-700">Certificates <span className="font-normal text-gray-500">(competency for permits)</span></label>
                      <button
                        type="button"
                        onClick={() => setModal((m) => ({ ...m, data: { ...m.data, certifications: [...(m.data.certifications || []), { name: '', certificate_number: '', issuing_authority: '', expiry_date: '' }] } }))}
                        className="text-xs px-2 py-1 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >+ Add Certificate</button>
                    </div>
                    {(modal.data.certifications || []).length > 0 && (
                      <div className="space-y-2">
                        {(modal.data.certifications || []).map((c, i) => {
                          const isPast = c.expiry_date && c.expiry_date < new Date().toISOString().slice(0, 10);
                          const upd = (key, val) => setModal((m) => ({ ...m, data: { ...m.data, certifications: (m.data.certifications || []).map((x, j) => j === i ? { ...x, [key]: val } : x) } }));
                          return (
                            <div key={i} className="grid grid-cols-12 gap-2 items-start">
                              <input value={c.name || ''} onChange={(e) => upd('name', e.target.value)} placeholder="Certification name" className="col-span-4 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
                              <input value={c.certificate_number || ''} onChange={(e) => upd('certificate_number', e.target.value)} placeholder="Certificate no." className="col-span-3 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
                              <input value={c.issuing_authority || ''} onChange={(e) => upd('issuing_authority', e.target.value)} placeholder="Issuing authority" className="col-span-2 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
                              <div className="col-span-2">
                                <input type="date" value={c.expiry_date || ''} min={new Date().toISOString().slice(0, 10)} onChange={(e) => upd('expiry_date', e.target.value)} className={`w-full px-2 py-1.5 border rounded-lg text-sm ${isPast ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} title="Expiry (cannot be past)" />
                              </div>
                              <button type="button" onClick={() => setModal((m) => ({ ...m, data: { ...m.data, certifications: (m.data.certifications || []).filter((_, j) => j !== i) } }))} className="col-span-1 text-red-600 hover:text-red-800 text-sm">✕</button>
                              {isPast && <p className="col-span-12 text-xs text-red-600 -mt-1">Expiry date cannot be in the past.</p>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <button onClick={() => setModal(null)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={save} disabled={saving || !modal.data.name} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:bg-blue-300 inline-flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', className = '' }) => (
  <div className={className}>
    <label className="block text-xs text-gray-600 mb-1">{label}</label>
    <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
  </div>
);

const SelectField = ({ label, value, onChange, options, allowBlank }) => (
  <div>
    <label className="block text-xs text-gray-600 mb-1">{label}</label>
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
      {allowBlank && <option value="">—</option>}
      {options.map((o) => typeof o === 'string'
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

export default ContractorManagement;
